/**
 * Driver Assignment Service - Fleet Premium
 * Servico de atribuicao de motoristas a rotas
 *
 * Funcionalidades:
 * - Atribuir motorista a uma rota/veiculo
 * - Listar motoristas disponiveis
 * - Filtrar rotas por motorista
 * - Gerenciar status de aceite
 * - Notificar motorista
 */

import routePlanService from '../../common/services/routePlanService';

// ============================================================================
// CONSTANTS
// ============================================================================

export const ASSIGNMENT_STATUS = {
  PENDING: 'pending',       // Aguardando aceite do motorista
  ACCEPTED: 'accepted',     // Motorista aceitou
  REJECTED: 'rejected',     // Motorista recusou
  IN_PROGRESS: 'in_progress', // Em execucao
  COMPLETED: 'completed',   // Concluido
  CANCELLED: 'cancelled',   // Cancelado
};

export const NOTIFICATION_TYPE = {
  NEW_ASSIGNMENT: 'new_assignment',
  ASSIGNMENT_UPDATED: 'assignment_updated',
  ASSIGNMENT_CANCELLED: 'assignment_cancelled',
  ROUTE_CHANGED: 'route_changed',
};

// ============================================================================
// DRIVER MANAGEMENT
// ============================================================================

/**
 * Lista todos os motoristas disponiveis
 * @returns {Promise<Array>} Lista de motoristas
 */
export const getAvailableDrivers = async () => {
  try {
    const response = await fetch('/api/drivers', {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch drivers: ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    console.error('[DriverAssignment] Error fetching drivers:', error);
    throw error;
  }
};

/**
 * Busca motorista por ID
 * @param {number} driverId - ID do motorista
 * @returns {Promise<Object>} Dados do motorista
 */
export const getDriver = async (driverId) => {
  try {
    const response = await fetch(`/api/drivers/${driverId}`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch driver: ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    console.error('[DriverAssignment] Error fetching driver:', error);
    throw error;
  }
};

/**
 * Lista motoristas com suas atribuicoes atuais
 * @returns {Promise<Array>} Lista de motoristas com status
 */
export const getDriversWithAssignments = async () => {
  try {
    // Buscar motoristas
    const drivers = await getAvailableDrivers();

    // Buscar planos em execucao
    const executingPlans = await routePlanService.getRoutePlans({ status: 'executing' });

    // Para cada motorista, verificar se tem atribuicao ativa
    const driversWithStatus = await Promise.all(
      drivers.map(async (driver) => {
        // Verificar se o motorista tem alguma rota ativa
        const activeAssignment = executingPlans.find((plan) => {
          // Verificar nos veiculos do plano
          return plan.driverId === driver.id;
        });

        return {
          ...driver,
          hasActiveAssignment: !!activeAssignment,
          activeAssignment: activeAssignment || null,
        };
      })
    );

    return driversWithStatus;
  } catch (error) {
    console.error('[DriverAssignment] Error fetching drivers with assignments:', error);
    throw error;
  }
};

// ============================================================================
// ASSIGNMENT MANAGEMENT
// ============================================================================

/**
 * Atribui um motorista a um veiculo em um plano
 * Usa o endpoint /api/route-plan-vehicles/{id}/assign-driver que envia push notification automaticamente
 *
 * @param {number} planId - ID do plano
 * @param {number} planVehicleId - ID do veiculo no plano
 * @param {number} driverId - ID do motorista
 * @param {Object} options - Opcoes adicionais
 * @returns {Promise<Object>} Atribuicao criada
 */
export const assignDriverToRoute = async (planId, planVehicleId, driverId, _options = {}) => {
  try {
    // Usar o novo endpoint que envia push notification automaticamente
    const response = await fetch(
      `/api/route-plan-vehicles/${planVehicleId}/assign-driver?driverId=${driverId}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to assign driver: ${response.statusText}`);
    }

    const vehicle = await response.json();

    // Registrar evento de atribuicao (para historico no web)
    try {
      await routePlanService.addRoutePlanEvent(planId, {
        type: 'driver_assigned',
        severity: 'info',
        message: `Motorista atribuido a rota`,
        vehicleId: planVehicleId,
        driverId,
        timestamp: new Date().toISOString(),
      });
    } catch (eventError) {
      console.warn('[DriverAssignment] Failed to create event:', eventError);
      // Nao falhar a atribuicao se criacao de evento falhar
    }

    // Push notification ja e enviado pelo backend automaticamente

    return {
      success: true,
      vehicle,
      driverId,
      status: ASSIGNMENT_STATUS.PENDING,
    };
  } catch (error) {
    console.error('[DriverAssignment] Error assigning driver:', error);
    throw error;
  }
};

/**
 * Remove atribuicao de motorista
 * Usa o endpoint /api/route-plan-vehicles/{id}/unassign-driver
 *
 * @param {number} planId - ID do plano
 * @param {number} planVehicleId - ID do veiculo no plano
 * @param {string} reason - Motivo da remocao
 * @returns {Promise<Object>} Resultado
 */
export const unassignDriver = async (planId, planVehicleId, reason = '') => {
  try {
    // Buscar veiculo atual para obter driverId (para registro de evento)
    const vehicles = await routePlanService.getRoutePlanVehicles(planId);
    const vehicle = vehicles.find((v) => v.id === planVehicleId);
    const previousDriverId = vehicle?.driverId;

    // Usar o novo endpoint
    const response = await fetch(
      `/api/route-plan-vehicles/${planVehicleId}/unassign-driver`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to unassign driver: ${response.statusText}`);
    }

    // Registrar evento
    try {
      await routePlanService.addRoutePlanEvent(planId, {
        type: 'driver_unassigned',
        severity: 'info',
        message: `Motorista removido da rota: ${reason || 'Sem motivo'}`,
        vehicleId: planVehicleId,
        driverId: previousDriverId,
        timestamp: new Date().toISOString(),
      });
    } catch (eventError) {
      console.warn('[DriverAssignment] Failed to create event:', eventError);
    }

    return { success: true };
  } catch (error) {
    console.error('[DriverAssignment] Error unassigning driver:', error);
    throw error;
  }
};

/**
 * Transfere rota para outro motorista
 * Usa o endpoint /api/route-plan-vehicles/{id}/assign-driver que envia push notification automaticamente
 *
 * @param {number} planId - ID do plano
 * @param {number} planVehicleId - ID do veiculo
 * @param {number} newDriverId - ID do novo motorista
 * @param {string} reason - Motivo da transferencia
 * @returns {Promise<Object>} Resultado
 */
export const transferRoute = async (planId, planVehicleId, newDriverId, reason = '') => {
  try {
    // Buscar veiculo atual para obter o motorista anterior
    const vehicles = await routePlanService.getRoutePlanVehicles(planId);
    const vehicle = vehicles.find((v) => v.id === planVehicleId);
    const previousDriverId = vehicle?.driverId;

    // Usar o endpoint de assign-driver (ele detecta que e uma transferencia se ja tinha motorista)
    const response = await fetch(
      `/api/route-plan-vehicles/${planVehicleId}/assign-driver?driverId=${newDriverId}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to transfer route: ${response.statusText}`);
    }

    // Registrar evento de transferencia
    try {
      await routePlanService.addRoutePlanEvent(planId, {
        type: 'route_transferred',
        severity: 'warning',
        message: `Rota transferida: ${reason || 'Sem motivo'}`,
        vehicleId: planVehicleId,
        fromDriverId: previousDriverId,
        toDriverId: newDriverId,
        timestamp: new Date().toISOString(),
      });
    } catch (eventError) {
      console.warn('[DriverAssignment] Failed to create transfer event:', eventError);
    }

    // Push notification para o novo motorista ja e enviado pelo backend automaticamente

    // Notificar motorista anterior sobre a transferencia
    if (previousDriverId) {
      try {
        await notifyDriver(previousDriverId, NOTIFICATION_TYPE.ASSIGNMENT_CANCELLED, {
          routePlanId: planId,
          vehicleId: planVehicleId,
          reason: reason || 'Rota transferida para outro motorista',
          transferredTo: newDriverId,
        });
      } catch (notifyError) {
        console.warn('[DriverAssignment] Failed to notify previous driver:', notifyError);
      }
    }

    return { success: true, previousDriverId, newDriverId };
  } catch (error) {
    console.error('[DriverAssignment] Error transferring route:', error);
    throw error;
  }
};

// ============================================================================
// ACCEPTANCE MANAGEMENT
// ============================================================================

/**
 * Motorista aceita a atribuicao
 * @param {number} planId - ID do plano
 * @param {number} planVehicleId - ID do veiculo
 * @returns {Promise<Object>} Resultado
 */
export const acceptAssignment = async (planId, planVehicleId) => {
  try {
    await routePlanService.updateRoutePlanVehicle(planId, planVehicleId, {
      assignmentStatus: ASSIGNMENT_STATUS.ACCEPTED,
      acceptedAt: new Date().toISOString(),
    });

    await routePlanService.addRoutePlanEvent(planId, {
      type: 'assignment_accepted',
      severity: 'info',
      message: 'Motorista aceitou a atribuicao',
      vehicleId: planVehicleId,
      timestamp: new Date().toISOString(),
    });

    return { success: true, status: ASSIGNMENT_STATUS.ACCEPTED };
  } catch (error) {
    console.error('[DriverAssignment] Error accepting assignment:', error);
    throw error;
  }
};

/**
 * Motorista recusa a atribuicao
 * @param {number} planId - ID do plano
 * @param {number} planVehicleId - ID do veiculo
 * @param {string} reason - Motivo da recusa
 * @returns {Promise<Object>} Resultado
 */
export const rejectAssignment = async (planId, planVehicleId, reason = '') => {
  try {
    await routePlanService.updateRoutePlanVehicle(planId, planVehicleId, {
      assignmentStatus: ASSIGNMENT_STATUS.REJECTED,
      rejectedAt: new Date().toISOString(),
      rejectionReason: reason,
    });

    await routePlanService.addRoutePlanEvent(planId, {
      type: 'assignment_rejected',
      severity: 'warning',
      message: `Motorista recusou a atribuicao: ${reason || 'Sem motivo'}`,
      vehicleId: planVehicleId,
      timestamp: new Date().toISOString(),
    });

    return { success: true, status: ASSIGNMENT_STATUS.REJECTED };
  } catch (error) {
    console.error('[DriverAssignment] Error rejecting assignment:', error);
    throw error;
  }
};

// ============================================================================
// FILTERING
// ============================================================================

/**
 * Busca rotas atribuidas a um motorista
 * @param {number} driverId - ID do motorista
 * @param {Object} filters - Filtros adicionais
 * @returns {Promise<Array>} Lista de rotas do motorista
 */
export const getDriverRoutes = async (driverId, filters = {}) => {
  try {
    // Buscar todos os planos
    const allPlans = await routePlanService.getRoutePlans(filters);

    // Filtrar planos onde o motorista esta atribuido
    const driverPlans = [];

    for (const plan of allPlans) {
      const vehicles = await routePlanService.getRoutePlanVehicles(plan.id);
      const driverVehicle = vehicles.find((v) => v.driverId === driverId);

      if (driverVehicle) {
        driverPlans.push({
          ...plan,
          assignedVehicle: driverVehicle,
          assignmentStatus: driverVehicle.assignmentStatus,
        });
      }
    }

    return driverPlans;
  } catch (error) {
    console.error('[DriverAssignment] Error fetching driver routes:', error);
    throw error;
  }
};

/**
 * Busca rotas do motorista logado (minhas rotas)
 * @param {Object} filters - Filtros adicionais
 * @returns {Promise<Array>} Lista de minhas rotas
 */
export const getMyRoutes = async (filters = {}) => {
  try {
    // Buscar usuario atual
    const userResponse = await fetch('/api/session', {
      credentials: 'include',
    });

    if (!userResponse.ok) {
      throw new Error('Failed to get current user');
    }

    const user = await userResponse.json();

    // Se o usuario tem um driverId associado, filtrar por ele
    // Caso contrario, buscar driver pelo userId
    let driverId = user.attributes?.driverId;

    if (!driverId) {
      // Tentar encontrar driver associado ao usuario
      const drivers = await getAvailableDrivers();
      const userDriver = drivers.find(
        (d) => d.attributes?.userId === user.id || d.uniqueId === user.email
      );
      driverId = userDriver?.id;
    }

    if (!driverId) {
      // Usuario nao e motorista, retornar lista vazia
      return [];
    }

    return getDriverRoutes(driverId, filters);
  } catch (error) {
    console.error('[DriverAssignment] Error fetching my routes:', error);
    throw error;
  }
};

/**
 * Verifica se o usuario atual e motorista
 * @returns {Promise<Object|null>} Dados do motorista ou null
 */
export const getCurrentDriverInfo = async () => {
  try {
    const userResponse = await fetch('/api/session', {
      credentials: 'include',
    });

    if (!userResponse.ok) {
      return null;
    }

    const user = await userResponse.json();
    let driverId = user.attributes?.driverId;

    if (!driverId) {
      const drivers = await getAvailableDrivers();
      const userDriver = drivers.find(
        (d) => d.attributes?.userId === user.id || d.uniqueId === user.email
      );
      if (userDriver) {
        return userDriver;
      }
      return null;
    }

    return getDriver(driverId);
  } catch (error) {
    console.error('[DriverAssignment] Error getting current driver info:', error);
    return null;
  }
};

// ============================================================================
// NOTIFICATIONS
// ============================================================================

/**
 * Envia notificacao para motorista
 * @param {number} driverId - ID do motorista
 * @param {string} type - Tipo de notificacao
 * @param {Object} data - Dados da notificacao
 * @returns {Promise<Object>} Resultado
 */
export const notifyDriver = async (driverId, type, data = {}) => {
  try {
    // Buscar dados do motorista para obter contatos
    const driver = await getDriver(driverId);

    // Tentar enviar por diferentes canais

    // 1. Notificacao no sistema (sempre)
    await createSystemNotification(driverId, type, data);

    // 2. Email (se disponivel)
    if (driver.attributes?.email) {
      try {
        await sendEmailNotification(driver.attributes.email, type, data);
      } catch (e) {
        console.warn('[DriverAssignment] Email notification failed:', e);
      }
    }

    // 3. Push notification (se disponivel)
    if (driver.attributes?.pushToken) {
      try {
        await sendPushNotification(driver.attributes.pushToken, type, data);
      } catch (e) {
        console.warn('[DriverAssignment] Push notification failed:', e);
      }
    }

    return { success: true };
  } catch (error) {
    console.error('[DriverAssignment] Error notifying driver:', error);
    throw error;
  }
};

/**
 * Cria notificacao no sistema
 * @param {number} driverId - ID do motorista
 * @param {string} type - Tipo
 * @param {Object} data - Dados
 */
const createSystemNotification = async (driverId, type, data) => {
  try {
    await fetch('/api/notifications', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        type: `route_${type}`,
        recipientType: 'driver',
        recipientId: driverId,
        title: getNotificationTitle(type),
        message: data.message || getDefaultMessage(type),
        data: JSON.stringify(data),
        read: false,
        createdAt: new Date().toISOString(),
      }),
    });
  } catch (error) {
    console.error('[DriverAssignment] Error creating system notification:', error);
    // Nao propagar erro - notificacao e best-effort
  }
};

/**
 * Envia email de notificacao
 * @param {string} email - Email do destinatario
 * @param {string} type - Tipo
 * @param {Object} data - Dados
 */
const sendEmailNotification = async (email, type, data) => {
  if (!email) return;

  try {
    const response = await fetch('/api/notifications/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        notificator: 'mail',
        email,
        subject: getNotificationTitle(type),
        body: getDefaultMessage(type),
        data: {
          type,
          routePlanId: data.routePlanId,
          driverId: data.driverId,
          ...data,
        },
      }),
    });

    if (!response.ok) {
      console.warn('[DriverAssignment] Email notification failed:', response.status);
    }
  } catch (error) {
    console.error('[DriverAssignment] Email notification error:', error);
  }
};

/**
 * Envia push notification
 * @param {string} token - Token do dispositivo
 * @param {string} type - Tipo
 * @param {Object} data - Dados
 */
const sendPushNotification = async (token, type, data) => {
  if (!token) return;

  try {
    const response = await fetch('/api/notifications/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        notificator: 'push',
        token,
        title: getNotificationTitle(type),
        body: getDefaultMessage(type),
        data: {
          type,
          routePlanId: data.routePlanId,
          driverId: data.driverId,
          click_action: data.routePlanId ? `/driver/route/${data.routePlanId}` : '/driver/routes',
          ...data,
        },
      }),
    });

    if (!response.ok) {
      console.warn('[DriverAssignment] Push notification failed:', response.status);
    }
  } catch (error) {
    console.error('[DriverAssignment] Push notification error:', error);
  }
};

/**
 * Retorna titulo da notificacao
 * @param {string} type - Tipo
 * @returns {string} Titulo
 */
const getNotificationTitle = (type) => {
  const titles = {
    [NOTIFICATION_TYPE.NEW_ASSIGNMENT]: 'Nova Rota Atribuida',
    [NOTIFICATION_TYPE.ASSIGNMENT_UPDATED]: 'Rota Atualizada',
    [NOTIFICATION_TYPE.ASSIGNMENT_CANCELLED]: 'Atribuicao Cancelada',
    [NOTIFICATION_TYPE.ROUTE_CHANGED]: 'Rota Alterada',
  };
  return titles[type] || 'Notificacao de Rota';
};

/**
 * Retorna mensagem padrao
 * @param {string} type - Tipo
 * @returns {string} Mensagem
 */
const getDefaultMessage = (type) => {
  const messages = {
    [NOTIFICATION_TYPE.NEW_ASSIGNMENT]: 'Voce foi atribuido a uma nova rota. Acesse o sistema para mais detalhes.',
    [NOTIFICATION_TYPE.ASSIGNMENT_UPDATED]: 'Sua rota foi atualizada. Verifique as alteracoes.',
    [NOTIFICATION_TYPE.ASSIGNMENT_CANCELLED]: 'Sua atribuicao foi cancelada.',
    [NOTIFICATION_TYPE.ROUTE_CHANGED]: 'A rota foi alterada. Verifique o novo trajeto.',
  };
  return messages[type] || 'Voce tem uma nova notificacao de rota.';
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Driver Management
  getAvailableDrivers,
  getDriver,
  getDriversWithAssignments,
  // Assignment Management
  assignDriverToRoute,
  unassignDriver,
  transferRoute,
  // Acceptance
  acceptAssignment,
  rejectAssignment,
  // Filtering
  getDriverRoutes,
  getMyRoutes,
  getCurrentDriverInfo,
  // Notifications
  notifyDriver,
  // Constants
  ASSIGNMENT_STATUS,
  NOTIFICATION_TYPE,
};
